import { Component, computed, effect, OnDestroy, OnInit, signal } from '@angular/core';
import { PlatformService, FamilyService, AuthenticationService, UserService, FormHelperService } from '@app/services';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { BoardCard, BoardConfig } from '@app/models/board-card';
import { forkJoin, of, Subject } from 'rxjs';
import { AssmatService } from '@app/services/assmat.service';
import { AccountType, User } from '@app/models/user';
import { BaseConfigService } from '@app/services/config/base.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Link } from '@app/models/custom-link';
import { ProgrammePersoService } from '@app/services/programme-perso.service';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  standalone: false
})
export class AccountComponent implements OnInit, OnDestroy {

  boardCards: BoardCard[];
  accountVars: any;
  loading = false;
  destroy$ = new Subject<void>();
  userValue: User;
  derniereModifConfAssmat: any;
  derniereModifConfFamily: any;
  localBoardHomeConfAssmat: any;
  localBoardHomeConfFamily: any;
  boardLink: Link[];
  idFamille: number;
  messagesFromProgPerso: { type: string, message: string } = {
    type: "",
    message: ""
  };
  confAssmat: BoardConfig;
  confEnseignant: BoardConfig;
  confFamily: BoardConfig;
  familleSignal = signal<number>(null);
  typeAccountSignal = signal<AccountType>(null);
  combinedSignal = computed(() => ({
    idFamille: this.familleSignal(),
    typeAccount: this.typeAccountSignal()
  }));

  constructor(
    private familyService: FamilyService,
    private accountService: AuthenticationService,
    private adulteService: UserService,
    public platformService: PlatformService,
    private assmatService: AssmatService,
    private baseConfigService: BaseConfigService,
    private router: Router,
    private programmePersoService: ProgrammePersoService,
    private helperService: FormHelperService,
    private activatedRoute: ActivatedRoute
  ) {
    effect(() => {
      const combinedValue = this.combinedSignal();
      const accountConfig = {
        assmat: this.confAssmat,
        enseignant: this.confEnseignant,
        family: this.confFamily,
      };

      const config: BoardConfig = accountConfig[this.typeAccountSignal()];
      const routeCategoryId = this.activatedRoute.snapshot.paramMap.get('idCategorie');

      if (routeCategoryId && config) {
        const category = config.cards.find(card => card.id === routeCategoryId && card.type === 'category');
        if (!category) {
          this.router.navigate([`/account`])
        }
      }

      if (!routeCategoryId && config?.programsOnDisplayAccount?.length > 0) {
        this.executeProgramPerso(combinedValue);
      }
    });
  }

  ngOnInit() {
    this.accountService.accountTypeChange$
      .pipe(
        tap(_ => {
          this.loading = true;
          this.messagesFromProgPerso.message = "";
        }),
        takeUntil(this.destroy$))
      .subscribe(typeAccount => {
        this.typeAccountSignal.set(typeAccount);

        switch (typeAccount) {
          default: // or = 'noAccount'
            this.loading = false;
            break;

          case 'assmat':
            this.confAssmat = this.baseConfigService.getFirstConf("board-assmat").content as BoardConfig;
            setTimeout(() => {
              if (this.accountService.currentUserValue) {
                forkJoin([
                  this.assmatService.getTypeEtablissementsEnCoursByIdAssmat(this.accountService.currentUserValue?.idAssmat),
                  this.assmatService.getAssmatVariables(this.accountService.currentUserValue?.idAssmat)
                ]).subscribe(([typeEtabs, accountVars]) => {
                  const boardCards = this.confAssmat.cards;
                  this.accountVars = accountVars;
                  this.filterBoardCardsByTypeEtabsEnCoursAssmat(typeEtabs, boardCards);
                  this.loading = false;
                })
              }
            });
            break;
          case 'enseignant':
            this.confEnseignant = this.baseConfigService.getFirstConf("board-enseignant").content as BoardConfig;
            this.boardCards = this.confEnseignant.cards.filter(card => card.enabled);
            this.loading = false;
            break;
          case 'family':
            this.confFamily = this.baseConfigService.getFirstConf("board-user").content as BoardConfig;
            this.confFamily.cards?.forEach(card => {
              if (card.component === 'reservations') {
                if (!this.accountService.currentUserValue || this.accountService.currentUserValue.idAdulte < 1) {
                  card.disabledCause = 'reservation.info.invalide_account_message';
                } else if (!!this.adulteService.currentAdulte && !this.adulteService.currentAdulte.emailConfirm) {
                  card.disabledCause = 'Votre email doit être confirmé';
                }
              }

              if (card.component === 'mandats-sepa') {
                if (!this.accountService.currentUserValue || this.accountService.currentUserValue.idAdulte < 1 || this.familyService.currentFamily.id < 1) {
                  card.disabledCause = 'mandat_sepa.info.invalide_account_message';
                }
              }
            });
            this.boardCards = this.confFamily.cards.filter(card => card.enabled);
            this.loading = false;
            break;

          case 'admin':
            this.router.navigate(['admin']);
        }
      });

    // Could add these info to basic familyData (that we get right after login), but conflicts with local storage ...
    this.familyService.currentFamilyChange$.pipe(
      tap(f => {
        if (f) {
          this.familleSignal.set(f.id)
        }
      }),
      takeUntil(this.destroy$),
      switchMap(fam => fam ? this.familyService.getFamilyVariables(fam.id) : of({}))
    ).subscribe((vars: any) => {
      this.accountVars = vars;
      this.familyService.reservationsToPay$.next(vars.reservationsEnAttentePaiement);
    });
  }

  executeProgramPerso(data: { idFamille: number, typeAccount: string }) {
    if (data.typeAccount) {
      this.programmePersoService.executeProgrammeAccountDisplay(data).subscribe((result: any) => {

        if (result) {
          this.helperService.displayDebugTraces(result?.traces);

          if (result?.messages && !result?.errors) {
            this.displayMessageProgPerso("message", result.messages)
          }

          if (result?.errors && result?.errors.length > 0) {
            this.displayMessageProgPerso("error", result.errors)
          }
        }
      })
    }
  }

  displayMessageProgPerso(type: string, messages: string[]) {
    if (messages.length > 1) {
      messages.forEach(mess => this.messagesFromProgPerso.message += `<li>${mess}</li>`);
    } else {
      this.messagesFromProgPerso.message = messages[0];
    }
    this.messagesFromProgPerso.type = type;
  }

  filterBoardCardsByTypeEtabsEnCoursAssmat(typeEtab, boardCards) {
    if (!typeEtab?.mentalo && typeEtab?.mikado) {
      this.boardCards = boardCards.filter(card => card.component !== "contrat-accueil-mentalo" &&
        card.component !== "animations" &&
        card.component !== "reunions" &&
        card.enabled);
    }
    if (!typeEtab?.mikado && typeEtab?.mentalo) {
      this.boardCards = boardCards.filter(card => card.component !== "assmat-pointage" && card.enabled);

    }
    if (typeEtab?.mentalo && typeEtab?.mikado) {
      this.boardCards = boardCards.filter(card => card.enabled);
    }

    if ((!typeEtab?.mentalo && !typeEtab?.mikado) || !typeEtab) {
      this.boardCards = boardCards.filter(card => (
        card.component !== "assmat-pointage" &&
        card.component !== "contrat-accueil-mentalo" &&
        card.component !== "reunions" &&
        card.component !== "animations" &&
        card.enabled))
    }
  }

  //filter to select the cards by etablissement
  filterBoardCardsByEtablissements() {

    if (this.typeAccountSignal() === "assmat") {
      return this.boardCards?.filter(card => !card.isNotPublic || this.accountVars.etablissements?.some((idEtab: number) => card.etablissementsIds?.includes(idEtab)))
    }

    return this.boardCards?.filter(card =>
      !card.isNotPublic || card.accueilsIds?.some(id =>
        this.accountVars.etablissementAccueil?.some((accEtb) => accEtb.accueils?.includes(id)))
    )

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
